/**
 * Module Creation Form component for trAInedup.ai
 * Allows admins and instructors to create course modules
 */

import React, { useState } from 'react';
import { FileUpload } from '../common/FileUpload';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import {
  Save,
  X,
  FileText,
  Video,
  Headphones,
  BookOpen,
  HelpCircle,
  Plus,
  Trash2,
} from 'lucide-react';
import { Button } from '../common/Button';
import { Input } from '../common/Input';
import { Card } from '../common/Card';
import { Module, ModuleType, QuizQuestionType } from '../../types/course';
import { RichTextEditor } from '../common/RichTextEditor';

/**
 * Simple quiz question interface for form data
 */
interface SimpleQuizQuestion {
  id: string;
  question: string;
  type: QuizQuestionType;
  options: string[];
  correctAnswer: number | boolean | string | string[] | number[];
  explanation?: string;
  points: number;
  leftItems?: string[];
  rightItems?: string[];
  blankPlaceholder?: string;
}

/**
 * Module form data interface
 */
interface ModuleFormData {
  title: string;
  description: string;
  type: ModuleType;
  estimatedDuration: number;
  content: string;
  videoUrl?: string;
  audioUrl?: string;
  pdfUrl?: string;
  quizQuestions: SimpleQuizQuestion[];
  order: number;
}

/**
 * Props interface
 */
interface ModuleCreationFormProps {
  courseId: string;
  moduleId: string; // Add moduleId prop for consistent file paths
  existingModules: Module[];
  onSave: (moduleData: ModuleFormData) => Promise<void>;
  onCancel: () => void;
  editingModule?: Module;
  insertPosition?: number | null; // Position where module should be inserted
}

/**
 * Module creation form component
 */
export const ModuleCreationForm: React.FC<ModuleCreationFormProps> = ({
  courseId: _courseId,
  moduleId: _moduleId,
  existingModules,
  onSave,
  onCancel,
  editingModule,
  insertPosition,
}) => {
  const [formData, setFormData] = useState<ModuleFormData>({
    title: editingModule?.title || '',
    description: editingModule?.description || '',
    type: editingModule?.type || ModuleType.TEXT,
    estimatedDuration: editingModule?.estimatedDuration || 10,
    content: (() => {
      if (!editingModule?.content) return '';
      // For text modules, content is an object with text field
      if (typeof editingModule.content === 'object' && editingModule.content.text) {
        return editingModule.content.text;
      }
      // For backward compatibility with old structure where content was a string
      if (typeof editingModule.content === 'string') return editingModule.content;
      // For other modules, content is an object with a text field (previously textContent)
      return editingModule.content?.text || editingModule.content?.textContent || '';
    })(),
    videoUrl: (typeof editingModule?.content === 'object' ? editingModule?.content?.videoUrl : '') || '',
    audioUrl: (typeof editingModule?.content === 'object' ? editingModule?.content?.audioUrl : '') || '',
    pdfUrl: (typeof editingModule?.content === 'object' ? editingModule?.content?.pdfUrl : '') || '',
    quizQuestions: (() => {
      if (!editingModule?.content || editingModule.type !== ModuleType.QUIZ) return [];
      if (typeof editingModule.content === 'object' && editingModule.content.quiz?.questions) {
        return editingModule.content.quiz.questions.map((q, _index) => ({
          id: q.id,
          question: q.question,
          type: q.type, // Use the actual type from the loaded question
          options: (q as any).options || ["", "", "", ""],
          correctAnswer: q.correctAnswer, // Use the actual correct answer
          explanation: q.explanation || "",
          points: q.points || 1,
          leftItems: (q as any).leftItems || [],
          rightItems: (q as any).rightItems || [],
          blankPlaceholder: (q as any).blankPlaceholder || "",
        }));
      }
      return [];
    })(),
    order: editingModule?.order ?? insertPosition ?? existingModules.length,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [audioUploading, setAudioUploading] = useState(false);
  const [videoUploading, setVideoUploading] = useState(false);
  const [pdfUploading, setPdfUploading] = useState(false);
  // ...existing code...
  // Use the moduleId passed from parent component for consistent file paths
  const moduleId = _moduleId;

  // Upload handlers
  const handleAudioUpload = async (file: File) => {
    setAudioUploading(true);
    try {
      const storage = getStorage();
      const storageRef = ref(storage, `courses/${_courseId}/${moduleId}/audio/en/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      setFormData(prev => ({ ...prev, audioUrl: url }));
    } catch (err) {
      setError('Failed to upload audio');
    } finally {
      setAudioUploading(false);
    }
  };

  const handleVideoUpload = async (file: File) => {
    setVideoUploading(true);
    try {
      const storage = getStorage();
      const storageRef = ref(storage, `courses/${_courseId}/${moduleId}/video/en/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      setFormData(prev => ({ ...prev, videoUrl: url }));
    } catch (err) {
      setError('Failed to upload video');
    } finally {
      setVideoUploading(false);
    }
  };

  const handlePdfUpload = async (file: File) => {
    setPdfUploading(true);
    try {
      const storage = getStorage();
      const storageRef = ref(storage, `courses/${_courseId}/${moduleId}/pdf/en/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      setFormData(prev => ({ ...prev, pdfUrl: url }));
    } catch (err) {
      setError('Failed to upload PDF');
    } finally {
      setPdfUploading(false);
    }
  };

  /**
   * Handle form input changes
   */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : value,
    }));
  };

  /**
   * Handle module type change
   */
  const handleTypeChange = (newType: ModuleType) => {
    setFormData(prev => ({
      ...prev,
      type: newType,
      // Clear type-specific fields when changing type
      videoUrl: newType === ModuleType.VIDEO ? prev.videoUrl : '',
      audioUrl: newType === ModuleType.AUDIO ? prev.audioUrl : '',
      pdfUrl: newType === ModuleType.PDF ? prev.pdfUrl : '',
      quizQuestions: newType === ModuleType.QUIZ ? prev.quizQuestions : [],
    }));
  };

  /**
   * Add quiz question
   */
  const addQuizQuestion = () => {
    const newQuestion: SimpleQuizQuestion = {
      id: `question-${Date.now()}`,
      question: "",
      type: QuizQuestionType.MULTIPLE_CHOICE, // Default to multiple choice
      options: ["", "", "", ""],
      correctAnswer: 0,
      explanation: "",
      points: 1,
    };
    setFormData(prev => ({
      ...prev,
      quizQuestions: [...prev.quizQuestions, newQuestion],
    }));
  };

  /**
   * Update quiz question
   */
  const updateQuizQuestion = (questionId: string, updates: Partial<SimpleQuizQuestion>) => {
    setFormData(prev => ({
      ...prev,
      quizQuestions: prev.quizQuestions.map(q =>
        q.id === questionId ? { ...q, ...updates } : q
      ),
    }));
  };

  /**
   * Update quiz question option
   */
  const updateQuizOption = (questionId: string, optionIndex: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      quizQuestions: prev.quizQuestions.map(q =>
        q.id === questionId
          ? {
              ...q,
              options: q.options.map((option, index) =>
                index === optionIndex ? value : option
              ),
            }
          : q
      ),
    }));
  };

  /**
   * Remove quiz question
   */
  const removeQuizQuestion = (questionId: string) => {
    setFormData(prev => ({
      ...prev,
      quizQuestions: prev.quizQuestions.filter(q => q.id !== questionId),
    }));
  };

  /**
   * Move quiz question up
   */
  const moveQuestionUp = (questionId: string) => {
    setFormData(prev => {
      const questions = [...prev.quizQuestions];
      const index = questions.findIndex(q => q.id === questionId);
      if (index > 0) {
        [questions[index], questions[index - 1]] = [questions[index - 1], questions[index]];
      }
      return { ...prev, quizQuestions: questions };
    });
  };

  /**
   * Move quiz question down
   */
  const moveQuestionDown = (questionId: string) => {
    setFormData(prev => {
      const questions = [...prev.quizQuestions];
      const index = questions.findIndex(q => q.id === questionId);
      if (index < questions.length - 1) {
        [questions[index], questions[index + 1]] = [questions[index + 1], questions[index]];
      }
      return { ...prev, quizQuestions: questions };
    });
  };

  /**
   * Insert new question below a specific question
   */
  const insertQuestionBelow = (questionId: string) => {
    const newQuestion: SimpleQuizQuestion = {
      id: `question-${Date.now()}`,
      question: "",
      type: QuizQuestionType.MULTIPLE_CHOICE, // Default to multiple choice
      options: ["", "", "", ""],
      correctAnswer: 0,
      explanation: "",
      points: 1,
    };
    
    setFormData(prev => {
      const questions = [...prev.quizQuestions];
      const index = questions.findIndex(q => q.id === questionId);
      questions.splice(index + 1, 0, newQuestion);
      return { ...prev, quizQuestions: questions };
    });
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e: React.FormEvent) => {

    e.preventDefault();

    // Prevent double submission
    if (isLoading) return;

    // Validation
    if (!formData.title.trim()) {
      setError('Module title is required');
      return;
    }

    if (!formData.description.trim()) {
      setError('Module description is required');
      return;
    }

    // Type-specific validation
    if (formData.type === ModuleType.VIDEO && !formData.videoUrl?.trim()) {
      setError('Video URL is required for video modules');
      return;
    }

    if (formData.type === ModuleType.AUDIO && !formData.audioUrl?.trim()) {
      setError('Audio URL is required for audio modules');
      return;
    }

    if (formData.type === ModuleType.PDF && !formData.pdfUrl?.trim()) {
      setError('PDF URL is required for PDF modules');
      return;
    }

    if (formData.type === ModuleType.TEXT && !formData.content.trim()) {
      setError('Content is required for text modules');
      return;
    }

    if (formData.type === ModuleType.QUIZ) {
      if (formData.quizQuestions.length === 0) {
        setError('At least one quiz question is required');
        return;
      }

      for (const question of formData.quizQuestions) {
        if (!question.question.trim()) {
          setError("All quiz questions must have a question text");
          return;
        }

        switch (question.type) {
          case QuizQuestionType.MULTIPLE_CHOICE:
          case QuizQuestionType.MULTIPLE_RESPONSE:
            if (question.options.length === 0 || question.options.some(option => !option.trim())) {
              setError("All options for multiple choice/response questions must be filled");
              return;
            }
            if (question.type === QuizQuestionType.MULTIPLE_CHOICE && typeof question.correctAnswer !== 'number') {
              setError("A correct answer must be selected for multiple choice questions");
              return;
            }
            if (question.type === QuizQuestionType.MULTIPLE_RESPONSE && (!Array.isArray(question.correctAnswer) || question.correctAnswer.length === 0)) {
              setError("At least one correct answer must be selected for multiple response questions");
              return;
            }
            break;
          case QuizQuestionType.TRUE_FALSE:
            if (typeof question.correctAnswer !== 'boolean') {
              setError("A correct answer (True/False) must be selected");
              return;
            }
            break;
          case QuizQuestionType.SHORT_ANSWER:
            if (!question.correctAnswer || (Array.isArray(question.correctAnswer) && question.correctAnswer.some(ans => typeof ans === 'string' && !ans.trim())) || (!Array.isArray(question.correctAnswer) && typeof question.correctAnswer === 'string' && !question.correctAnswer.trim())) {
              setError("Correct answer(s) must be provided for short answer questions");
              return;
            }
            break;
          case QuizQuestionType.MATCHING:
            if (!question.leftItems || question.leftItems.length === 0 || question.leftItems.some(item => !item.trim())) {
              setError("All left items must be filled for matching questions");
              return;
            }
            if (!question.rightItems || question.rightItems.length === 0 || question.rightItems.some(item => !item.trim())) {
              setError("All right items must be filled for matching questions");
              return;
            }
            if (!Array.isArray(question.correctAnswer) || question.correctAnswer.length !== question.leftItems.length || question.correctAnswer.some(ans => typeof ans !== 'number')) {
              setError("All matching pairs must be correctly set");
              return;
            }
            break;
          default:
            // Fallback for unknown types, or if options are expected
            if (question.options && question.options.some(option => !option.trim())) {
              setError("All quiz options must be filled");
              return;
            }
        }
      }
    }

    setIsLoading(true);
    setError('');

    try {
      // Build content based on module type
      let content: any;
      
      switch (formData.type) {
        case ModuleType.TEXT:
          // For text modules, content should be an object with html field
          content = {
            html: formData.content || '',
            text: formData.content || ''
          };
          break;
        case ModuleType.VIDEO:
          content = {
            videoUrl: formData.videoUrl || ''
          };
          if (formData.content) {
            content.text = formData.content;
          }
          break;
        case ModuleType.AUDIO:
          content = {
            audioUrl: formData.audioUrl || ''
          };
          if (formData.content) {
            content.text = formData.content;
          }
          break;
        case ModuleType.PDF:
          content = {
            pdfUrl: formData.pdfUrl || ''
          };
          if (formData.content) {
            content.text = formData.content;
          }
          break;
        case ModuleType.QUIZ:
          if (formData.quizQuestions && formData.quizQuestions.length > 0) {
            content = {
              quiz: {
                title: formData.title,
                questions: formData.quizQuestions.map((q, index) => {
                  const questionObj: any = {
                    id: q.id,
                    question: q.question,
                    type: q.type,
                    options: q.options,
                    correctAnswer: q.correctAnswer,
                    points: 1,
                    order: index
                  };
                  if (typeof q.explanation === 'string' && q.explanation.trim() !== '') {
                    questionObj.explanation = q.explanation;
                  }
                  return questionObj;
                }),
                passingScore: 70,
                maxAttempts: 3
              }
            };
          } else {
            content = {};
          }
          break;
        default:
          content = {};
      }

      const moduleData = {
        ...formData,
        content: content,
      };
      
      await onSave(moduleData);
    } catch (error: any) {
      setError(error.message || 'Failed to save module');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Get module type icon
   */
  const getModuleTypeIcon = (type: ModuleType) => {
    switch (type) {
      case ModuleType.VIDEO:
        return <Video className="w-5 h-5" />;
      case ModuleType.AUDIO:
        return <Headphones className="w-5 h-5" />;
      case ModuleType.PDF:
        return <FileText className="w-5 h-5" />;
      case ModuleType.TEXT:
        return <BookOpen className="w-5 h-5" />;
      case ModuleType.QUIZ:
        return <HelpCircle className="w-5 h-5" />;
      default:
        return <BookOpen className="w-5 h-5" />;
    }
  };

  /**
   * Render type-specific content fields
   */
  const renderTypeSpecificFields = () => {
    switch (formData.type) {
      case ModuleType.VIDEO:
        return (
          <div className="space-y-4">
            <FileUpload
              label="Video File"
              accept="video/*"
              onFileUpload={handleVideoUpload}
              disabled={videoUploading}
            />
            {formData.videoUrl && (
              <div className="mt-2">
                <video src={formData.videoUrl} controls className="h-32 rounded-lg border" />
              </div>
            )}
            {videoUploading && (
              <div className="text-sm text-neutral-600 mt-2">Uploading...</div>
            )}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Video Description (Optional)
              </label>
              <RichTextEditor
                value={formData.content}
                onChange={(value) => setFormData(prev => ({ ...prev, content: value }))}
                placeholder="Additional notes or description for this video"
              />
            </div>
          </div>
        );

      case ModuleType.AUDIO:
        return (
          <div className="space-y-4">
            <FileUpload
              label="Audio File"
              accept="audio/*"
              onFileUpload={handleAudioUpload}
              disabled={audioUploading}
            />
            {formData.audioUrl && (
              <div className="mt-2">
                <audio src={formData.audioUrl} controls className="w-full" />
              </div>
            )}
            {audioUploading && (
              <div className="text-sm text-neutral-600 mt-2">Uploading...</div>
            )}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Audio Description (Optional)
              </label>
              <RichTextEditor
                value={formData.content}
                onChange={(value) => setFormData(prev => ({ ...prev, content: value }))}
                placeholder="Additional notes or description for this audio"
              />
            </div>
          </div>
        );

      case ModuleType.PDF:
        return (
          <div className="space-y-4">
            <FileUpload
              label="PDF File"
              accept="application/pdf"
              onFileUpload={handlePdfUpload}
              disabled={pdfUploading}
            />
            {formData.pdfUrl && (
              <div className="mt-2">
                <a href={formData.pdfUrl} target="_blank" rel="noopener noreferrer" className="text-primary underline">View PDF</a>
              </div>
            )}
            {pdfUploading && (
              <div className="text-sm text-neutral-600 mt-2">Uploading...</div>
            )}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                PDF Description (Optional)
              </label>
              <RichTextEditor
                value={formData.content}
                onChange={(value) => setFormData(prev => ({ ...prev, content: value }))}
                placeholder="Additional notes or description for this PDF"
              />
            </div>
          </div>
        );

      case ModuleType.TEXT:
        return (
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Module Content *
            </label>
            <RichTextEditor
              value={formData.content}
              onChange={(value) => setFormData(prev => ({ ...prev, content: value }))}
              placeholder="Enter the text content for this module."
            />
          </div>
        );

      case ModuleType.QUIZ:
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-neutral-900">Quiz Questions</h3>
              <Button type="button" variant="primary" icon={Plus} onClick={addQuizQuestion}>
                Add Question
              </Button>
            </div>

            {formData.quizQuestions.length === 0 ? (
              <div className="text-center py-8 border-2 border-dashed border-neutral-300 rounded-lg">
                <HelpCircle className="w-12 h-12 text-neutral-400 mx-auto mb-4" />
                <h3 className="font-medium text-neutral-900 mb-2">No questions added yet</h3>
                <p className="text-neutral-600 mb-4">
                  Add questions to create your quiz module
                </p>
                <Button type="button" variant="primary" icon={Plus} onClick={addQuizQuestion}>
                  Add First Question
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {formData.quizQuestions.map((q, index) => (
              <Card key={q.id} className="p-4 space-y-4">
                <div className="flex justify-between items-center">
                  <h4 className="font-medium text-neutral-800">Question {index + 1}</h4>
                  <div className="flex items-center space-x-2">
                    <select
                      value={q.type}
                      onChange={(e) => updateQuizQuestion(q.id, { type: e.target.value as QuizQuestionType })}
                      className="border border-neutral-300 rounded-lg px-3 py-2 text-sm"
                    >
                      {Object.values(QuizQuestionType).map((type) => (
                        <option key={type} value={type}>
                          {type.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                        </option>
                      ))}
                    </select>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => moveQuestionUp(q.id)}
                      disabled={index === 0}
                    >
                      Up
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => moveQuestionDown(q.id)}
                      disabled={index === formData.quizQuestions.length - 1}
                    >
                      Down
                    </Button>
                    <Button
                      type="button"
                      variant="danger"
                      size="sm"
                      icon={Trash2}
                      onClick={() => removeQuizQuestion(q.id)}
                    >
                      Remove
                    </Button>
                  </div>
                </div>
                <Input
                  label="Question Text *"
                  value={q.question}
                  onChange={(e) => updateQuizQuestion(q.id, { question: e.target.value })}
                  placeholder="Enter the question text"
                  required
                />

                {/* Conditional rendering based on question type */}
                {q.type === QuizQuestionType.MULTIPLE_CHOICE && (
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-neutral-700">Options *</label>
                    {q.options.map((option, optIndex) => (
                      <div key={optIndex} className="flex items-center space-x-2">
                        <input
                          type="radio"
                          name={`correctAnswer-${q.id}`}
                          checked={q.correctAnswer === optIndex}
                          onChange={() => updateQuizQuestion(q.id, { correctAnswer: optIndex })}
                          className="form-radio text-primary-600"
                        />
                        <Input
                          value={option}
                          onChange={(e) => updateQuizOption(q.id, optIndex, e.target.value)}
                          placeholder={`Option ${optIndex + 1}`}
                          required
                        />
                      </div>
                    ))}
                    <Button type="button" variant="secondary" size="sm" onClick={() => updateQuizQuestion(q.id, { options: [...q.options, ''] })}>
                      Add Option
                    </Button>
                  </div>
                )}

                {q.type === QuizQuestionType.TRUE_FALSE && (
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-neutral-700">Correct Answer *</label>
                    <div className="flex items-center space-x-4">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name={`correctAnswer-${q.id}`}
                          checked={q.correctAnswer === true}
                          onChange={() => updateQuizQuestion(q.id, { correctAnswer: true })}
                          className="form-radio text-primary-600"
                        />
                        <span className="ml-2">True</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name={`correctAnswer-${q.id}`}
                          checked={q.correctAnswer === false}
                          onChange={() => updateQuizQuestion(q.id, { correctAnswer: false })}
                          className="form-radio text-primary-600"
                        />
                        <span className="ml-2">False</span>
                      </label>
                    </div>
                  </div>
                )}

                {q.type === QuizQuestionType.SHORT_ANSWER && (
                  <div className="space-y-2">
                    <Input
                      label="Correct Answer(s) * (comma-separated for multiple)"
                      value={Array.isArray(q.correctAnswer) ? (q.correctAnswer as string[]).join(', ') : q.correctAnswer as string}
                      onChange={(e) => updateQuizQuestion(q.id, { correctAnswer: e.target.value.split(',').map(s => s.trim()) })}
                      placeholder="e.g., dog, canine"
                      required
                    />
                    <Input
                      label="Blank Placeholder (Optional)"
                      value={q.blankPlaceholder || ''}
                      onChange={(e) => updateQuizQuestion(q.id, { blankPlaceholder: e.target.value })}
                      placeholder="e.g., Type your answer here"
                    />
                  </div>
                )}

                {q.type === QuizQuestionType.MULTIPLE_RESPONSE && (
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-neutral-700">Options * (Select all correct)</label>
                    {q.options.map((option, optIndex) => (
                      <div key={optIndex} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          name={`correctAnswer-${q.id}`}
                          checked={Array.isArray(q.correctAnswer) && (q.correctAnswer as number[]).includes(optIndex)}
                          onChange={(e) => {
                            const currentCorrect = Array.isArray(q.correctAnswer) ? [...(q.correctAnswer as number[])] : [];
                            if (e.target.checked) {
                              updateQuizQuestion(q.id, { correctAnswer: [...currentCorrect, optIndex].sort((a, b) => a - b) });
                            } else {
                              updateQuizQuestion(q.id, { correctAnswer: currentCorrect.filter(item => item !== optIndex) });
                            }
                          }}
                          className="form-checkbox text-primary-600"
                        />
                        <Input
                          value={option}
                          onChange={(e) => updateQuizOption(q.id, optIndex, e.target.value)}
                          placeholder={`Option ${optIndex + 1}`}
                          required
                        />
                      </div>
                    ))}
                    <Button type="button" variant="secondary" size="sm" onClick={() => updateQuizQuestion(q.id, { options: [...q.options, ''] })}>
                      Add Option
                    </Button>
                  </div>
                )}

                {q.type === QuizQuestionType.MATCHING && (
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-neutral-700">Matching Pairs *</label>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <h5 className="text-sm font-medium text-neutral-600 mb-1">Left Items</h5>
                        {q.leftItems?.map((item, itemIndex) => (
                          <Input
                            key={`left-${itemIndex}`}
                            value={item}
                            onChange={(e) => {
                              const newLeftItems = [...(q.leftItems || [])];
                              newLeftItems[itemIndex] = e.target.value;
                              updateQuizQuestion(q.id, { leftItems: newLeftItems });
                            }}
                            placeholder={`Left Item ${itemIndex + 1}`}
                            className="mb-2"
                          />
                        ))}
                        <Button type="button" variant="secondary" size="sm" onClick={() => updateQuizQuestion(q.id, { leftItems: [...(q.leftItems || []), ''] })}>
                          Add Left Item
                        </Button>
                      </div>
                      <div>
                        <h5 className="text-sm font-medium text-neutral-600 mb-1">Right Items</h5>
                        {q.rightItems?.map((item, itemIndex) => (
                          <Input
                            key={`right-${itemIndex}`}
                            value={item}
                            onChange={(e) => {
                              const newRightItems = [...(q.rightItems || [])];
                              newRightItems[itemIndex] = e.target.value;
                              updateQuizQuestion(q.id, { rightItems: newRightItems });
                            }}
                            placeholder={`Right Item ${itemIndex + 1}`}
                            className="mb-2"
                          />
                        ))}
                        <Button type="button" variant="secondary" size="sm" onClick={() => updateQuizQuestion(q.id, { rightItems: [...(q.rightItems || []), ''] })}>
                          Add Right Item
                        </Button>
                      </div>
                    </div>
                    <label className="block text-sm font-medium text-neutral-700 mt-4">Correct Pairings *</label>
                    <p className="text-sm text-neutral-500 mb-2">For each Left Item, select the corresponding Right Item.</p>
                    {q.leftItems?.map((leftItem, leftIndex) => (
                      <div key={`pairing-${leftIndex}`} className="flex items-center space-x-2 mb-2">
                        <span className="w-1/3 text-right pr-2">{leftItem || `Left Item ${leftIndex + 1}`}:</span>
                        <select
                          value={typeof q.correctAnswer === 'object' && q.correctAnswer !== null && (q.correctAnswer as number[])[leftIndex] !== undefined ? (q.correctAnswer as number[])[leftIndex] : ''}
                          onChange={(e) => {
                            const newCorrectAnswer = Array.isArray(q.correctAnswer) ? [...(q.correctAnswer as number[])] : Array(q.leftItems?.length || 0).fill(null);
                            newCorrectAnswer[leftIndex] = parseInt(e.target.value, 10);
                            updateQuizQuestion(q.id, { correctAnswer: newCorrectAnswer });
                          }}
                          className="border border-neutral-300 rounded-lg px-3 py-2 text-sm flex-grow"
                        >
                          <option value="">Select Right Item</option>
                          {q.rightItems?.map((rightItem, rightIndex) => (
                            <option key={rightIndex} value={rightIndex}>
                              {rightItem}
                            </option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                )}

                <Input
                  label="Explanation (Optional)"
                  value={q.explanation || ''}
                  onChange={(e) => updateQuizQuestion(q.id, { explanation: e.target.value })}
                  placeholder="Provide an explanation for the correct answer"
                />
                <Input
                  label="Points *"
                  type="number"
                  value={q.points}
                  onChange={(e) => updateQuizQuestion(q.id, { points: parseInt(e.target.value, 10) || 0 })}
                  required
                />
                <Button type="button" variant="secondary" size="sm" onClick={() => insertQuestionBelow(q.id)}>
                  Insert Question Below
                </Button>
              </Card>
                ))}
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-neutral-800 mb-2">
          {editingModule ? 'Edit Module' : 'Create New Module'}
        </h2>
        <p className="text-neutral-600">
          {editingModule 
            ? 'Update the module details' 
            : insertPosition !== null && insertPosition !== undefined
              ? `Add a new module at position ${insertPosition + 1}`
              : 'Add a new module to your course'
          }
        </p>
        {insertPosition !== null && insertPosition !== undefined && (
          <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-blue-800 text-sm">
              <strong>Insertion Position:</strong> This module will be inserted at position {insertPosition + 1}. 
              All modules after this position will be moved down.
            </p>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <Card className="p-8 mb-6">
          <div className="space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Module Title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Enter module title"
                required
              />

              <Input
                label="Estimated Duration (minutes)"
                name="estimatedDuration"
                type="number"
                min="1"
                value={formData.estimatedDuration}
                onChange={handleInputChange}
                placeholder="e.g., 15"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Module Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={3}
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Brief description of what this module covers"
                required
              />
            </div>

            {/* Module Type Selection */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-3">
                Module Type
              </label>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {Object.values(ModuleType).map(type => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => handleTypeChange(type)}
                    className={`p-4 border-2 rounded-lg transition-colors ${
                      formData.type === type
                        ? 'border-primary bg-primary-50 text-primary-800'
                        : 'border-neutral-200 hover:border-neutral-300 text-neutral-700'
                    }`}
                  >
                    <div className="flex flex-col items-center space-y-2">
                      {getModuleTypeIcon(type)}
                      <span className="text-sm font-medium capitalize">{type}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Type-specific Fields */}
            {renderTypeSpecificFields()}
          </div>
        </Card>

        {/* Action Buttons */}
        <div className="flex items-center justify-between">
          <Button
            type="button"
            variant="ghost"
            icon={X}
            onClick={onCancel}
          >
            Cancel
          </Button>

          <Button
            type="submit"
            variant="primary"
            icon={Save}
            loading={isLoading}
            disabled={isLoading}
          >
            {isLoading 
              ? (editingModule ? 'Updating Module...' : 'Creating Module...') 
              : (editingModule ? 'Update Module' : 'Create Module')
            }
          </Button>
        </div>
      </form>
    </div>
  );
};

